import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Download, Info, UserX } from 'lucide-react';
import { merchantExperienceApi, extractErrorMessage, formatDateTime, formatNumber } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type {
  ExperienceReservationStatus,
  MerchantExperienceParticipantDto,
  MerchantExperienceSessionDto,
  Tone,
} from '@kardume/types';
import {
  EXPERIENCE_NO_SHOW_ERROR_MESSAGES,
  EXPERIENCE_RESERVATION_STATUS_LABELS,
} from '@kardume/types';
import { ActionButton, Badge, Button, Modal, Table, TableContainer } from '@kardume/ui';
import { useAsync } from '../../utils/useAsync';
import DataState from '../DataState';
import { formatSessionRange, isPast } from '../../utils/experienceSchedule';

/**
 * A lista de quem vai à data (card #348) - a tela que a loja abre **no dia**.
 *
 * ## Duas listas, não uma
 *
 * Confirmados e fila de espera aparecem separados porque significam coisas
 * diferentes: um tem lugar, o outro está esperando alguém desistir. Misturados,
 * a loja arruma mesa a mais.
 *
 * ## Falta não é cancelamento
 *
 * Cancelamento devolve a vaga; **não comparecimento não devolve** - o lugar
 * ficou separado e a loja não pôde vendê-lo. Sem essa frase na tela o lojista
 * usa um pelo outro, e a lotação histórica passa a mentir.
 *
 * ## O que esta tela deliberadamente NÃO mostra
 *
 * O token do QR Code. Ele não vem na resposta e não deve ser pedido: com o
 * token visível, qualquer pessoa da equipe registraria a presença de quem não
 * veio, e o check-in deixaria de provar que alguém esteve na loja.
 */

const Head = styled.div`
  display: grid;
  gap: 0.5rem;
  margin-bottom: 1rem;

  .when {
    font-size: 0.92rem;
    font-weight: 600;
    color: ${(p) => p.theme.textPrimary};
  }
`;

const Summary = styled.div`
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
`;

const Notice = styled.div`
  display: flex;
  gap: 0.55rem;
  align-items: flex-start;
  padding: 0.7rem 0.85rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  font-size: 0.82rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textSecondary};

  svg {
    flex-shrink: 0;
    margin-top: 0.1rem;
    color: ${(p) => p.theme.primary};
  }
  strong {
    color: ${(p) => p.theme.textPrimary};
  }
`;

const Section = styled.section`
  margin-top: 1.25rem;

  h4 {
    margin: 0 0 0.5rem;
    font-size: 0.82rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${(p) => p.theme.textMuted};
  }
`;

const Empty = styled.p`
  margin: 0;
  padding: 0.9rem;
  font-size: 0.85rem;
  color: ${(p) => p.theme.textMuted};
  border: 1px dashed ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const ConfirmText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.55;
  color: ${(p) => p.theme.textSecondary};

  strong {
    color: ${(p) => p.theme.textPrimary};
  }

  & + & {
    margin-top: 0.75rem;
  }
`;

function statusTone(status: ExperienceReservationStatus): Tone {
  if (status === 'CHECKED_IN') return 'success';
  if (status === 'CONFIRMED') return 'info';
  if (status === 'WAITLISTED') return 'warning';
  if (status === 'NO_SHOW') return 'danger';
  return 'secondary';
}

/** Uma célula de CSV: aspas dobradas e o campo inteiro entre aspas. */
const csvCell = (value: string): string => `"${value.replace(/"/g, '""')}"`;

interface Props {
  session: MerchantExperienceSessionDto | null;
  experienceTitle: string;
  canManage: boolean;
  onClose: () => void;
  /** Marcar falta muda contadores da agenda - o detalhe precisa reler. */
  onChanged: () => Promise<void> | void;
}

const SessionParticipantsModal: React.FC<Props> = ({
  session,
  experienceTitle,
  canManage,
  onClose,
  onChanged,
}) => {
  const { showNotification } = useNotification();
  const [noShowTarget, setNoShowTarget] = useState<MerchantExperienceParticipantDto | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { data, loading, error, reload } = useAsync(
    () => (session ? merchantExperienceApi.participants(session.id) : Promise.resolve([])),
    [session?.id]
  );

  const participants = useMemo(() => data ?? [], [data]);

  const confirmed = participants.filter(
    (item) => item.status === 'CONFIRMED' || item.status === 'CHECKED_IN'
  );
  const waitlisted = participants
    .filter((item) => item.status === 'WAITLISTED')
    .sort((a, b) => (a.waitlistPosition ?? 0) - (b.waitlistPosition ?? 0));
  const closed = participants.filter(
    (item) => item.status === 'CANCELED' || item.status === 'NO_SHOW'
  );

  const started = session ? isPast(session.startsAt) : false;

  const markNoShow = async (): Promise<void> => {
    if (!noShowTarget) return;
    setSubmitting(true);
    try {
      await merchantExperienceApi.markNoShow(noShowTarget.reservationId);
      showNotification('Não comparecimento registrado.', 'success');
      setNoShowTarget(null);
      await reload();
      await onChanged();
    } catch (err) {
      showNotification(
        extractErrorMessage(
          err,
          'Não foi possível marcar o não comparecimento.',
          EXPERIENCE_NO_SHOW_ERROR_MESSAGES
        ),
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Exporta a lista da noite. O arquivo é montado e baixado no navegador de
   * quem está autenticado - nada é publicado nem ganha link: a lista é dado
   * pessoal de cliente.
   */
  const exportCsv = (): void => {
    if (!session) return;
    const header = ['Nome', 'Situação', 'Posição na fila', 'Reservou em', 'Check-in em'];
    const lines = participants.map((item) =>
      [
        item.customerName,
        EXPERIENCE_RESERVATION_STATUS_LABELS[item.status],
        item.waitlistPosition != null ? String(item.waitlistPosition) : '',
        formatDateTime(item.reservedAt),
        item.checkedInAt ? formatDateTime(item.checkedInAt) : '',
      ]
        .map(csvCell)
        .join(';')
    );
    // BOM para o Excel em pt-BR abrir os acentos corretamente.
    const content = `﻿${[header.map(csvCell).join(';'), ...lines].join('\r\n')}`;
    const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `participantes-${new Date(session.startsAt).toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const renderTable = (
    rows: MerchantExperienceParticipantDto[],
    options: { showActions: boolean }
  ): React.ReactNode => (
    <TableContainer>
      <Table>
        <thead>
          <tr>
            <th>Pessoa</th>
            <th>Situação</th>
            <th>Reservou em</th>
            <th>Check-in</th>
            {options.showActions && <th style={{ textAlign: 'right' }}>Ações</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((item) => (
            <tr key={item.reservationId}>
              <td>{item.customerName || 'Cliente'}</td>
              <td>
                <Badge tone={statusTone(item.status)}>
                  {EXPERIENCE_RESERVATION_STATUS_LABELS[item.status]}
                  {item.status === 'WAITLISTED' && item.waitlistPosition != null
                    ? ` · ${item.waitlistPosition}º`
                    : ''}
                </Badge>
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(item.reservedAt)}</td>
              <td style={{ whiteSpace: 'nowrap' }}>
                {item.checkedInAt ? formatDateTime(item.checkedInAt) : '-'}
              </td>
              {options.showActions && (
                <td style={{ textAlign: 'right' }}>
                  {/* Só reserva CONFIRMED vira falta: quem fez check-in esteve
                      na loja, e o core recusa desfazer presença. */}
                  {canManage && item.status === 'CONFIRMED' && (
                    <ActionButton
                      aria-label="Marcar não comparecimento"
                      title={
                        started
                          ? 'Marcar que não compareceu'
                          : 'Só depois de a data começar'
                      }
                      color="#DC2626"
                      disabled={!started}
                      onClick={() => setNoShowTarget(item)}
                    >
                      <UserX size={16} />
                    </ActionButton>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    </TableContainer>
  );

  return (
    <>
      <Modal
        isOpen={session !== null}
        onClose={onClose}
        title="Participantes da data"
        width="820px"
      >
        {session && (
          <>
            <Head>
              <span className="when">
                {experienceTitle} - {formatSessionRange(session.startsAt, session.endsAt)}
              </span>
              <Summary>
                <Badge tone="info">
                  {formatNumber(session.reservedCount)} de {formatNumber(session.capacity)} lugares
                </Badge>
                <Badge tone="success">{formatNumber(session.checkedInCount)} presentes</Badge>
                <Badge tone="warning">
                  {formatNumber(session.waitlistCount)} na fila de espera
                </Badge>
              </Summary>
              <Notice>
                <Info size={15} />
                <span>
                  <strong>Falta não devolve a vaga</strong> - o lugar ficou separado e não pôde ser
                  vendido. Cancelamento devolve. Use "não compareceu" só para quem reservou e não
                  apareceu, e apenas depois de a data começar.
                </span>
              </Notice>
            </Head>

            <DataState
              loading={loading}
              error={error}
              isEmpty={participants.length === 0}
              emptyTitle="Ninguém reservou esta data ainda"
              emptyDescription="Quando alguém reservar pelo app ou pelo site, o nome aparece aqui."
              onRetry={reload}
            >
              <Section>
                <h4>Com lugar garantido ({formatNumber(confirmed.length)})</h4>
                {confirmed.length === 0 ? (
                  <Empty>Nenhuma reserva confirmada nesta data.</Empty>
                ) : (
                  renderTable(confirmed, { showActions: true })
                )}
              </Section>

              <Section>
                <h4>Fila de espera ({formatNumber(waitlisted.length)})</h4>
                {waitlisted.length === 0 ? (
                  <Empty>
                    Ninguém esperando. A fila só se forma quando a data lota - e ela anda sozinha
                    quando alguém cancela.
                  </Empty>
                ) : (
                  renderTable(waitlisted, { showActions: false })
                )}
              </Section>

              {closed.length > 0 && (
                <Section>
                  <h4>Cancelamentos e faltas ({formatNumber(closed.length)})</h4>
                  {renderTable(closed, { showActions: false })}
                </Section>
              )}

              {participants.length > 0 && (
                <Toolbar>
                  <Button variant="outline" onClick={exportCsv}>
                    <Download size={16} /> Baixar a lista (CSV)
                  </Button>
                </Toolbar>
              )}
            </DataState>
          </>
        )}
      </Modal>

      <Modal
        isOpen={noShowTarget !== null}
        onClose={() => setNoShowTarget(null)}
        title="Marcar não comparecimento"
        width="500px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setNoShowTarget(null)} disabled={submitting}>
              Voltar
            </Button>
            <Button variant="danger" onClick={markNoShow} isLoading={submitting}>
              Não compareceu
            </Button>
          </>
        }
      >
        <ConfirmText>
          Registrar que <strong>{noShowTarget?.customerName}</strong> não compareceu?
        </ConfirmText>
        <ConfirmText>
          A vaga <strong>não volta</strong> para a data: o lugar ficou separado e a loja não pôde
          vendê-lo. Se a intenção é liberar o lugar para outra pessoa, o caminho é o cancelamento -
          feito por quem reservou ou pela loja, antes da data.
        </ConfirmText>
      </Modal>
    </>
  );
};

export default SessionParticipantsModal;
